<template>
  <div class="table-responsive pivot-datatable">
    <table ref="pivotDataTable" :class="['table', striped ? 'table-striped' : '', hover ? 'table-hover' : '', smallRows ? 'table-sm' : '']" style="width:100%;">
      <thead>
        <tr>
          <th
            v-for="(column, index) of dataColumns"
            :key="column.id"
            :class="getColumnHeaderClass(index)"
            @click="columnHeaderClicked(index)">
            {{column.id}}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row of dataSetRows" :key="row.id">
          <td v-for="column of dataColumns" :key="column.id">
            <slot name="rowfield" :data="{ row: row, column: column }" v-if="!column.column" />
            <slot name="cell" :data="row[column.id]" v-if="column.column" />
          </td>
        </tr>
      </tbody>
    </table>
    <p class="font-italic text-center text-muted" v-if="dataSetRows.length == 0">
      <slot name="empty" />
    </p>
  </div>
</template>

<style>
.pivot-datatable {
  overflow: visible;    /* JB: Popovers & draggable items cause issues if this isn't set this way */
}
.table tbody tr.pivot-datatable-row-nostripe {
  background-color: transparent;
}
.table tbody tr.pivot-datatable-row-nostripe:hover td {
  background-color: transparent;
}
.pivot-datatable .pivot-datatable-striped {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Sort Icons */
.pivot-datatable > table > thead .sorting,
.pivot-datatable > table > thead .sorting_asc,
.pivot-datatable > table > thead .sorting_desc {
  cursor: pointer;
  position: relative;
}
.pivot-datatable > table > thead .sorting:after {
  font-family: "Font Awesome 5 Pro";
  opacity: 0.2;
  content: "\f0dc";
  font-weight: 900;
}
.pivot-datatable > table > thead .sorting_asc:after {
  font-family: "Font Awesome 5 Pro";
  content: "\f0de";
  font-weight: 900;
}
.pivot-datatable > table > thead .sorting_desc:after {
  font-family: "Font Awesome 5 Pro";
  content: "\f0dd";
  font-weight: 900;
}
.pivot-datatable > table > thead .sorting:after,
.pivot-datatable > table > thead .sorting_asc:after,
.pivot-datatable > table > thead .sorting_desc:after {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: block;
}
</style>

<script>
import _ from 'lodash'

export default {
  name: "PivotDataTable",
  props: {
    dataset: Array,
    columns: Array,
    rowFields: Array,
    fields: Array,

    striped: {
      type: Boolean,
      default: true
    },
    hover: {
      type: Boolean,
      default: true
    },
    smallRows: {
      type: Boolean,
      default: false
    },
    defaultColumnOrderIndex: {
      type: Number,
      default: 0
    },
    defaultColumnOrderDir: {
      type: String,
      default: 'ASC'
    },
  },
  data() {
    return {
      dataSetRows: [],
      dataColumns: [],
      orderColumnIndex: this.$props.defaultColumnOrderIndex,
      orderColumnDir: this.$props.defaultColumnOrderDir,
    }
  },
  mounted() {
    this.updateDataset()    
  },
  beforeDestroy() {
   
  },
  watch: {
    dataset: function() {
      this.updateDataset()
    }
  },
  methods: {
    updateDataset() {
      this.dataSetRows = []
      this.dataColumns = []

      if(!this.dataset || !this.columns || !this.fields || !this.rowFields)
        return

      let columnMap = {}

      // Calculate pivot data
      this.dataset.forEach(data => {
        this.rowFields.forEach(rowField => {
          columnMap[rowField.name] = {
            id: rowField.name,
            field: [rowField.field],
            column: false
          }
        })

        let columnMapField = _.compact(this.columns.map(columnInfo => data[columnInfo.field])).join(',')
        let field = data[columnMapField]

        columnMap[columnMapField] = {
          id: columnMapField,
          field: this.columns.map(columnInfo => columnInfo.field),
          column: true,
          sortValue: this.columns.map(columnInfo => data[columnInfo.sortField])
        }
      })

      Object.keys(columnMap).forEach(field => {
        let colInfo = columnMap[field]

        this.dataColumns.push({
          id: colInfo.id,
          field: colInfo.field,
          column: colInfo.column,
          sortValue: colInfo.sortValue
        })
      })

      this.dataColumns.sort((c1, c2) => {
        if(c1.column && c2.column) {
          for(let i=0; i<c1.sortValue.length; i++) {
            if(c1.sortValue[i] != c2.sortValue[i])
              return c1.sortValue[i] - c2.sortValue[i]
          }
          return 0
        } else {
          return c1.column
        }
      })

      // Calculate rows
      let lookupMap = {}
      this.dataset.forEach(data => {
        let rowKey = this.rowFields.map(rowField => {
          return data[rowField.field]
        }).join(', ')

        let lookupIndex = lookupMap[rowKey] 
        
        let rowData = {
          id: rowKey,
          cellData: data
        }

        this.rowFields.forEach(rowField => {
          rowData[rowField.name] = data[rowField.field]
        })

        if(lookupIndex === undefined) {
          lookupMap[rowKey] = this.dataSetRows.length
          this.dataSetRows.push(rowData)
        } else {
          rowData = this.dataSetRows[lookupIndex]
        }

        this.dataColumns.forEach(col => {
          let value = _.compact(col.field.map(f => data[f])).join(',')

          if(value == col.id) {
            this.fields.forEach(dataField => {
              rowData[col.id] = data[dataField.field]
            })
          }
        })

        this.dataSetRows[lookupIndex] = rowData
      })

      // If an entire column is null, remove the column
      this.dataColumns = this.dataColumns.filter(col => {
        if(!col.column)
          return true
        if(this.dataSetRows.find(row => row[col.id] !== null && row[col.id] !== undefined) == null) {
          return false
        }
        return true
      })

      this.updateSortData()
    },
    updateSortData() {
      let sortColumn = this.dataColumns[this.orderColumnIndex]
      if(sortColumn === undefined)
        return

      this.dataSetRows.sort((row1, row2) => {
        let valA = row1[sortColumn.id] || 0
        let valB = row2[sortColumn.id] || 0

        if(this.orderColumnDir == 'ASC')
          return valA < valB ? -1 : (valA > valB ? 1 : 0)
        return valA > valB ? -1 : (valA < valB ? 1 : 0)
      })
    },
    getColumnHeaderClass(index) {
      var classList = []

      if(index != this.orderColumnIndex)
        classList.push('sorting')
      else if(this.orderColumnDir == 'ASC')
        classList.push('sorting_asc')
      else
        classList.push('sorting_desc')

      return classList
    },
    columnHeaderClicked(index) {

      if(index == this.orderColumnIndex) {
        if(this.orderColumnDir == 'ASC')
          this.orderColumnDir = 'DESC'
        else
          this.orderColumnDir = 'ASC'
      } else {
        this.orderColumnIndex = index
        this.orderColumnDir = 'ASC'
      }

      this.updateSortData()
    },
  }
};
</script>
